import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { IconButton } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";

import { useApiCall } from "@framework/react-hooks";
import { ProgressOverlay } from "@framework/mui-page-layout";
import { useAsu, RangeType } from "@framework/provider-asu";
import { IUserDto, useUser } from "@framework/provider-user";

import { StyledFormSelect } from "../../../../ui/form-select";
import { useFormatDateHumanReadable } from "../../../../../../../../hooks";
import { formatMeasurementNumber } from "../../../../utils/measurement-format";
import { StyledMeasurementPickerRow } from "./styled";

import {
  IAutocompletePointResponse,
  TLastMeasureAutocompleteResponse,
  IMeasureOption,
  ANALYTICS_MEASURE_TYPE,
  ANALYTICS_OBJECT,
} from "../../interfaces";

import {
  measureTypeName,
  objectName,
  idObjectName,
  idParameterName,
  idMeasureName,
  idMeasuresOptionsName,
} from "../../constants";
import {
  getIdMeasureFormValue,
  getMeasureSelectionKey,
  mergeMeasuresBySelectionKey,
  parseIdMeasureQueryParam,
} from "./utils";

export interface ISingleSelectWithArrows {
  loading?: boolean;
}

export const SingleSelectWithArrows: FC<ISingleSelectWithArrows> = ({ loading = false }) => {
  const normalizeResponseArray = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : []);

  const {
    selectedItem,
    startTimestamp,
    endTimestamp,
    userSelectedRangeType,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
    resetRecorderState,
    triggerRecorderReset,
    dbTz,
  } = useAsu();

  const [searchParams, setSearchParams] = useSearchParams();
  const measureTypeQueryParam = searchParams.get("measureType");
  const objectQueryParam = searchParams.get("object");
  const idObjectQueryParam = searchParams.get("idObject");
  const idParameterQueryParam = searchParams.get("idParameter");

  const form = useFormContext();
  const user = useUser<IUserDto>();
  const { formatMessage } = useIntl();
  const formatDate = useFormatDateHumanReadable();

  const mergedLastMeasuresRef = useRef<TLastMeasureAutocompleteResponse>({});

  const idTrain = useMemo(() => selectedItem && "TrainId" in selectedItem && selectedItem.TrainId, [selectedItem]);

  const measureType = useWatch({ name: measureTypeName });
  const object = useWatch({ name: objectName });
  const idObject = useWatch({ name: idObjectName });
  const idParameter = useWatch({ name: idParameterName });
  const idMeasure = useWatch({ name: idMeasureName });
  const idMeasuresOptions = useWatch({ name: idMeasuresOptionsName }) as IMeasureOption[];

  const isSpectrumSelected = measureType === ANALYTICS_MEASURE_TYPE.SPECTRUM;
  const isSignalFormSelected = measureType === ANALYTICS_MEASURE_TYPE.SIGNAL_FORM;
  const isOrbitSelected = measureType === ANALYTICS_MEASURE_TYPE.ORBIT;

  const isTrainSelected = object === ANALYTICS_OBJECT.TRAIN;
  const isMechanismsSelected = object === ANALYTICS_OBJECT.MECHANISM;
  const isComponentsSelected = object === ANALYTICS_OBJECT.NODE;
  const isPointsSelected = object === ANALYTICS_OBJECT.POINT;

  const updateSearchParams = useCallback(() => {
    if (
      (isSpectrumSelected || isSignalFormSelected || isOrbitSelected) &&
      idMeasure !== null &&
      idMeasure !== undefined
    ) {
      searchParams.set("idMeasure", idMeasure.toString());
      setSearchParams(searchParams);
    }
  }, [idMeasure, isSpectrumSelected, isSignalFormSelected, isOrbitSelected, searchParams, setSearchParams]);

  const processLastMeasuresRecorderMode = (
    newData: TLastMeasureAutocompleteResponse,
  ): TLastMeasureAutocompleteResponse => {
    const merged: TLastMeasureAutocompleteResponse = {
      ...mergedLastMeasuresRef.current,
    };
    for (const key in newData) {
      if (merged[key]) {
        merged[key] = {
          ...merged[key],
          data: mergeMeasuresBySelectionKey(merged[key].data, newData[key].data),
        };
      } else {
        merged[key] = newData[key];
      }
    }
    mergedLastMeasuresRef.current = merged;
    return mergedLastMeasuresRef.current;
  };

  const { fn: getLastMeasures, isLoading: isMeasuresLoading } = useApiCall(
    (api, { measureName, url, data: { startTimestamp, endTimestamp, idObject, ...idData } }) => {
      const _endTimestamp = new Date(new Date(endTimestamp).getTime() - dbTz * 60 * 1000).toISOString();

      const measureNames = JSON.stringify([measureName]);
      let points: IAutocompletePointResponse[] = [];

      return (
        api.fetchJson({
          url,
          method: "GET",
          data: { ...idData },
        }) as Promise<IAutocompletePointResponse[] | null | undefined>
      )
        .then(fetchedPoints => {
          points = normalizeResponseArray(fetchedPoints);
          const pointIds = isPointsSelected
            ? points.filter(({ id }) => id === idObject).map(({ id }) => id)
            : points.map(({ id }) => id);

          if (!pointIds.length) {
            form.setValue(idMeasuresOptionsName, [], { shouldDirty: true });
            form.setValue(idMeasureName, null, { shouldDirty: true });
            return null;
          }

          return api.fetchJson({
            url: "/point-v2/last-measures",
            method: "GET",
            data: {
              points: JSON.stringify(pointIds),
              measureNames,
              startTimestamp,
              endTimestamp: _endTimestamp,
              measureType,
            },
          }) as Promise<TLastMeasureAutocompleteResponse | null>;
        })
        .then(lastMeasures => {
          if (!lastMeasures) {
            return;
          }

          const measureData = normalizeResponseArray(lastMeasures.measureData1?.data);
          const measureName = lastMeasures.measureData1?.name ?? "";
          let merged;
          if (userSelectedRangeType === RangeType.RECORDER_MODE) {
            merged = processLastMeasuresRecorderMode({
              measureData1: {
                name: measureName,
                data: measureData,
              },
            });
          } else {
            merged = {
              measureData1: {
                name: measureName,
                data: measureData,
              },
            };
            mergedLastMeasuresRef.current = {};
          }

          const currentMeasureValue = form.getValues(idMeasureName) as number | number[] | null;
          const currentMeasuresOptions: IMeasureOption[] = form.getValues(idMeasuresOptionsName) ?? [];
          const selectedMeasureIndexes = (
            Array.isArray(currentMeasureValue) ? currentMeasureValue : [currentMeasureValue]
          ).filter((value): value is number => value !== null && value !== undefined);
          const selectedStableMeasureKeys = selectedMeasureIndexes
            .map(index => {
              const option = currentMeasuresOptions.find(currentOption => currentOption.id === index);
              return option ? getMeasureSelectionKey(option) : null;
            })
            .filter((value): value is string => value !== null);

          const transformedJson =
            merged.measureData1.data
              .sort((a, b) => new Date(b.measureDate).getTime() - new Date(a.measureDate).getTime())
              .slice(0, 20)
              .map((measurement, index) => {
                const formattedDate = formatDate(measurement.measureDate, { withSeconds: true });
                const formattedValue = formatMeasurementNumber(
                  measurement.value1,
                  measurement.units,
                  user.profile.interfaceSettings.dataFormat,
                  { fallbackDigits: 4 },
                );
                const localizedUnits = formatMessage({ id: `enums.units.${measurement.units}` });
                const pointTitle =
                  points.find(point => Number(point.id) === measurement.idPoint)?.title || "Unknown Point";
                const pointDirection = !isPointsSelected
                  ? formatMessage({
                      id: `enums.pointDirection.${
                        points.find(point => Number(point.id) === measurement.idPoint)?.Direction || ""
                      }`,
                    })
                  : "";

                return {
                  id: index,
                  title: `${formattedDate} - ${formattedValue} ${localizedUnits} - ${pointTitle}${
                    pointDirection ? " " + pointDirection : ""
                  }`,
                  idMeasure: measurement.idMeasure,
                  measureDate: measurement.measureDate,
                };
              }) || [];

          form.setValue(idMeasuresOptionsName, transformedJson, { shouldDirty: true });

          const restoredSelection = selectedStableMeasureKeys
            .map(selectedStableMeasureKey => {
              return transformedJson.find(option => getMeasureSelectionKey(option) === selectedStableMeasureKey)?.id;
            })
            .filter((value): value is number => value !== null && value !== undefined);
          const uniqueRestoredSelection = Array.from(new Set(restoredSelection));

          if (uniqueRestoredSelection.length > 0) {
            form.setValue(idMeasureName, getIdMeasureFormValue(uniqueRestoredSelection, isOrbitSelected), {
              shouldDirty: true,
            });
            return;
          }

          const idMeasureQueryValues = parseIdMeasureQueryParam(searchParams.get(idMeasureName));
          const foundMeasureIds = idMeasureQueryValues
            .map(id => transformedJson.find(measure => measure.id === id)?.id)
            .filter((value): value is number => value !== null && value !== undefined);

          if (foundMeasureIds.length > 0) {
            form.setValue(idMeasureName, getIdMeasureFormValue(foundMeasureIds, isOrbitSelected), {
              shouldDirty: true,
            });
            return;
          }

          if (transformedJson.length > 0) {
            form.setValue(idMeasureName, transformedJson[0].id, { shouldDirty: true });
          }
        })
        .catch(error => {
          console.error("Error fetching common points or last measures:", error);
        });
    },
    { success: false },
  );

  const idMeasureValue = Array.isArray(idMeasure) ? idMeasure[0] : idMeasure;

  const handlePrev = () => {
    const currentIdMeasure = idMeasuresOptions.find(option => option.id === idMeasureValue);
    const prevIndex = Number(currentIdMeasure?.id) - 1;
    const prevIdMeasure = idMeasuresOptions[prevIndex].id;
    form.setValue(idMeasureName, prevIdMeasure, { shouldDirty: true });
  };

  const handleNext = () => {
    const currentIdMeasure = idMeasuresOptions.find(option => option.id === idMeasureValue);
    const nextIndex = Number(currentIdMeasure?.id) + 1;
    const nextIdMeasure = idMeasuresOptions[nextIndex].id;
    form.setValue(idMeasureName, nextIdMeasure, { shouldDirty: true });
  };

  const handleRecorderSelectChange = useCallback((): Promise<void> => {
    if (userSelectedRangeType !== RangeType.RECORDER_MODE) {
      return Promise.resolve();
    }

    resetRecorderState();
    triggerRecorderReset();

    return Promise.resolve();
  }, [resetRecorderState, triggerRecorderReset, userSelectedRangeType]);

  const selectedMeasuresCount = Array.isArray(idMeasure) ? idMeasure.length : 1;

  const isLoading = loading || isMeasuresLoading;

  useEffect(() => {
    form.setValue(idMeasureName, null, { shouldDirty: true });
    form.setValue(idMeasuresOptionsName, [], { shouldDirty: true });
  }, [measureType, object, idObject, idParameter]);

  useEffect(() => {
    if (
      (isSpectrumSelected || isSignalFormSelected || isOrbitSelected) &&
      isPointsSelected &&
      idObject &&
      idParameter
    ) {
      const measureName = idParameter;
      void getLastMeasures(form, {
        measureName,
        url: "/point/train/autocomplete",
        data: {
          idTrain,
          startTimestamp,
          endTimestamp,
          idObject,
        },
      });
    }
    if (
      (isSpectrumSelected || isSignalFormSelected || isOrbitSelected) &&
      isComponentsSelected &&
      idObject &&
      idParameter
    ) {
      const measureName = idParameter;
      const idTrainComponent = idObject;
      void getLastMeasures(form, {
        measureName,
        url: "/point/component/autocomplete",
        data: {
          idTrainComponent,
          startTimestamp,
          endTimestamp,
          idObject,
        },
      });
    }
    if (
      (isSpectrumSelected || isSignalFormSelected || isOrbitSelected) &&
      isMechanismsSelected &&
      idObject &&
      idParameter
    ) {
      const measureName = idParameter;
      const idTrainMech = idObject;
      void getLastMeasures(form, {
        measureName,
        url: "/point/mechanism/autocomplete",
        data: {
          idTrainMech,
          startTimestamp,
          endTimestamp,
          idObject,
        },
      });
    }
    if ((isSpectrumSelected || isSignalFormSelected || isOrbitSelected) && isTrainSelected && idObject && idParameter) {
      const measureName = idParameter;
      const idTrain = idObject;
      void getLastMeasures(form, {
        measureName,
        url: "/point/train/autocomplete",
        data: {
          idTrain,
          startTimestamp,
          endTimestamp,
          idObject,
        },
      });
    }
  }, [measureType, object, idObject, idParameter, idTrain, startTimestamp, endTimestamp]);

  useEffect(() => {
    updateSearchParams();
  }, [idMeasure, updateSearchParams]);

  useEffect(() => {
    mergedLastMeasuresRef.current = {};
  }, [
    measureTypeQueryParam,
    objectQueryParam,
    idObjectQueryParam,
    idParameterQueryParam,
    idTrain,
    userSelectedRangeType,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
  ]);

  const renderControls = () => (
    <StyledMeasurementPickerRow>
      <IconButton
        onClick={handlePrev}
        disabled={idMeasureValue === 0 || idMeasureValue === null || selectedMeasuresCount > 1}
        sx={{ p: 0.5 }}
      >
        <ArrowBack />
      </IconButton>
      <StyledFormSelect
        name={idMeasureName}
        options={idMeasuresOptions}
        isLoading={isLoading}
        multiple={!isOrbitSelected}
        onChange={handleRecorderSelectChange}
      />
      <IconButton
        onClick={handleNext}
        disabled={
          idMeasureValue === idMeasuresOptions.length - 1 || idMeasureValue === null || selectedMeasuresCount > 1
        }
        sx={{ p: 0.5 }}
      >
        <ArrowForward />
      </IconButton>
    </StyledMeasurementPickerRow>
  );

  return <ProgressOverlay isLoading={isLoading}>{renderControls()}</ProgressOverlay>;
};
